import { addDaysYMD, todayISO } from "../lib/date";
import { calcAge } from "../domain/physiology";
import type { ActivityKey, GoalDirection, OnboardingDraft, PacePreset, Sex } from "./types";

export const ACTIVITY_OPTIONS: Array<{
  key: ActivityKey;
  factor: number;
  label: string;
  example: string;
}> = [
  { key: "sedentary", factor: 1.2, label: "Sedentary", example: "Desk job, very little walking" },
  { key: "light", factor: 1.3, label: "Lightly active", example: "Office work plus a couple of walks or workouts a week" },
  { key: "moderate", factor: 1.5, label: "Moderately active", example: "On your feet most days · 3–4 training sessions a week" },
  { key: "very-active", factor: 1.7, label: "Very active", example: "Physical job or daily training" },
  { key: "athlete", factor: 1.9, label: "Athlete", example: "High-volume training, 2-a-days, or labor-heavy work" },
];

export function activityByKey(key: ActivityKey): { factor: number; label: string } {
  const m = ACTIVITY_OPTIONS.find((o) => o.key === key);
  return m ? { factor: m.factor, label: m.label } : { factor: 1.3, label: "Lightly active" };
}

// Mifflin-St Jeor. For sex='X' we average the male and female adjustments
// (a defensible compromise when biological sex is withheld).
export function bmrFor({
  weightLbs,
  heightIn,
  sex,
  birthday,
}: {
  weightLbs: number;
  heightIn: number;
  sex: Sex;
  birthday: string;
}): number | null {
  const age = calcAge(birthday);
  if (!weightLbs || !heightIn || age === null) return null;
  const weightKg = weightLbs * 0.453592;
  const heightCm = heightIn * 2.54;
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  if (sex === "M") return base + 5;
  if (sex === "F") return base - 161;
  return base + (5 + -161) / 2; // average
}

export function tdeeFor(bmr: number, factor: number): number {
  return bmr * factor;
}

// Map a (goal, current, target) into a default pace if the user hasn't set one yet.
export function defaultPaceLbsPerWeek(goal: GoalDirection, currentLbs: number, targetLbs: number): number {
  if (goal === "maintain") return 0;
  const delta = Math.abs(targetLbs - currentLbs);
  // Default to ~0.75 lb/wk for lose, ~0.5 lb/wk for gain. Floor 0.25, ceiling 2.
  const ideal = goal === "lose" ? 0.75 : 0.5;
  if (delta < 5) return Math.max(0.25, ideal / 2);
  return Math.min(2, ideal);
}

export const PACE_PRESETS: Record<PacePreset, { label: string; lbsPerWeek: number; caution?: boolean }> = {
  gentle: { label: "Gentle", lbsPerWeek: 0.5 },
  moderate: { label: "Moderate", lbsPerWeek: 1.0 },
  aggressive: { label: "Aggressive", lbsPerWeek: 1.5 },
  faster: { label: "Faster", lbsPerWeek: 2.0, caution: true },
};

// ─────────────────────────────────────────────────────────────────────────
// Direction-aware pace bounds.
// Cutting up to ~2 lb/wk is sustainable for many lifters; gaining faster than
// ~1 lb/wk is mostly fat gain and not what a lean bulker wants. So the cap
// is asymmetric.
// ─────────────────────────────────────────────────────────────────────────

export function maxPaceForGoal(goal: GoalDirection): number {
  if (goal === "gain") return 1.0;
  if (goal === "lose") return 2.0;
  return 0;
}

/** Where the slider's red caution-band marker sits (~80% of max). */
export function cautionPaceForGoal(goal: GoalDirection): number {
  return maxPaceForGoal(goal) * 0.8;
}

/** Direction-aware preset list — gain mode halves the lose presets. */
export function pacePresetsForGoal(goal: GoalDirection): { preset: PacePreset; lbsPerWeek: number; label: string; caution?: boolean }[] {
  if (goal === "gain") {
    return [
      { preset: "gentle", lbsPerWeek: 0.25, label: "Gentle" },
      { preset: "moderate", lbsPerWeek: 0.5, label: "Moderate" },
      { preset: "aggressive", lbsPerWeek: 0.75, label: "Aggressive" },
      { preset: "faster", lbsPerWeek: 1.0, label: "Faster", caution: true },
    ];
  }
  if (goal === "lose") {
    return [
      { preset: "gentle", lbsPerWeek: 0.5, label: "Gentle" },
      { preset: "moderate", lbsPerWeek: 1.0, label: "Moderate" },
      { preset: "aggressive", lbsPerWeek: 1.5, label: "Aggressive" },
      { preset: "faster", lbsPerWeek: 2.0, label: "Faster", caution: true },
    ];
  }
  return [];
}

// ─────────────────────────────────────────────────────────────────────────
// Chart endpoint helper — gives the SVG y-coordinates so the line's slope
// is illustrative of the actual pace. Slow pace = shallow slope, fast pace
// = steep slope. Maintain = flat line at the chart's vertical centre.
// Returns viewBox-space y coords assuming a 110px tall chart with top/bot
// padding at 18/92.
// ─────────────────────────────────────────────────────────────────────────

export function chartEndpointsForPlan(goal: GoalDirection, paceLbsPerWeekAbs: number): { y0: number; y1: number } {
  const Y_TOP = 18;
  const Y_BOT = 92;
  const MID = (Y_TOP + Y_BOT) / 2;
  if (goal === "maintain") return { y0: MID, y1: MID };
  const maxPace = maxPaceForGoal(goal);
  const ratio = maxPace > 0 ? Math.min(1, Math.max(0, paceLbsPerWeekAbs / maxPace)) : 0;
  // Range: at min pace, line spans 40% of the chart vertically;
  // at max pace, line spans 100%. Just illustrative.
  const fullSpan = Y_BOT - Y_TOP;
  const span = fullSpan * (0.4 + 0.6 * ratio);
  const yHigh = MID - span / 2;
  const yLow = MID + span / 2;
  if (goal === "lose") return { y0: yHigh, y1: yLow };
  // gain
  return { y0: yLow, y1: yHigh };
}

// Daily target calories given pace and TDEE. ~3500 cal per lb of body mass.
// For "maintain", returns TDEE rounded. For loss the budget is below TDEE,
// for gain above. The 1200/1500 safety value is surfaced as a soft "Low
// intake" warning by display callers (DailyTargetsCard, SprintPlanSummary),
// not enforced here — pace is the hard clamp (capped upstream by the
// pace slider at maxPaceForGoal). Rounded to the nearest cal/day so the
// onboarding preview matches the sprint-editor recompute.
export function dailyTargetFor({
  goal,
  paceLbsPerWeek,
  tdee,
  sex: _sex,
}: {
  goal: GoalDirection;
  paceLbsPerWeek: number;
  tdee: number;
  sex: Sex;
}): number {
  if (goal === "maintain") return Math.round(tdee);
  const dailyDelta = (paceLbsPerWeek * 3500) / 7;
  const raw = goal === "lose" ? tdee - dailyDelta : tdee + dailyDelta;
  return Math.max(0, Math.round(raw));
}

// How many days to reach the target weight at this pace? Used to derive a
// projected end date for the plan reveal.
export function daysToTarget(currentLbs: number, targetLbs: number, paceLbsPerWeek: number): number {
  if (paceLbsPerWeek <= 0) return 0;
  const delta = Math.abs(targetLbs - currentLbs);
  return Math.ceil((delta / paceLbsPerWeek) * 7);
}

export function projectedEndDateYmd(currentLbs: number, targetLbs: number, paceLbsPerWeek: number): string {
  const days = daysToTarget(currentLbs, targetLbs, paceLbsPerWeek);
  if (days <= 0) return addDaysYMD(todayISO(), 84) ?? todayISO();
  return addDaysYMD(todayISO(), Math.max(7, days)) ?? todayISO();
}

// Effective pace = the user's requested pace. The pace slider already caps
// at maxPaceForGoal (2.0 lb/wk lose, 1.0 lb/wk gain), so pace IS the hard
// safety clamp. The calorie floor is a soft warning only — it doesn't
// silently slow the saved pace anymore. Both the dashboard's "Plan"
// (intake-derived) and "Original Plan" (linear-to-goal) lines now derive
// from this same requested pace, so they still align on day 1.
export function effectivePaceLbsPerWeek({
  paceLbsPerWeekAbs,
  tdee: _tdee,
  sex: _sex,
  goal,
}: {
  paceLbsPerWeekAbs: number;
  tdee: number;
  sex: Sex;
  goal: GoalDirection;
}): number {
  if (goal === "maintain") return 0;
  return paceLbsPerWeekAbs;
}

export function deriveGoalDirection(currentLbs: number, targetLbs: number): GoalDirection {
  const delta = targetLbs - currentLbs;
  if (Math.abs(delta) < 0.5) return "maintain";
  return delta < 0 ? "lose" : "gain";
}

// Auto-generate a sprint name like "Cut · May–Aug 2026".
export function autoSprintName(goal: GoalDirection, endYmd: string): string {
  const startD = new Date();
  const endD = new Date(endYmd + "T00:00:00");
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  const startM = fmt(startD);
  const endM = fmt(endD);
  const year = endD.getFullYear();
  const kind = goal === "lose" ? "Cut" : goal === "gain" ? "Bulk" : "Maintain";
  return `${kind} · ${startM}–${endM} ${year}`;
}

export function fullResultFromDraft(draft: OnboardingDraft) {
  const sex: Sex = draft.sex ?? "X";
  const goal: GoalDirection = draft.goalDirection ?? deriveGoalDirection(draft.currentWeightLbs, draft.targetWeightLbs);
  const bmr = bmrFor({
    weightLbs: draft.currentWeightLbs,
    heightIn: draft.heightIn,
    sex,
    birthday: draft.birthday,
  }) ?? 0;
  const tdee = bmr > 0 ? tdeeFor(bmr, draft.activityFactor) : 0;
  const dailyTarget = tdee > 0
    ? dailyTargetFor({ goal, paceLbsPerWeek: draft.paceLbsPerWeekAbs, tdee, sex })
    : 0;
  // Use the *effective* pace (after the safety floor) for the saved end-date
  // so the dashboard's "Plan" and "Original Plan" lines start together.
  const effPace = tdee > 0
    ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee, sex, goal })
    : draft.paceLbsPerWeekAbs;
  const endYmd = projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
  const sprintName = autoSprintName(goal, endYmd);
  return { bmr, tdee, dailyTarget, endYmd, sprintName, goal, sex, effectivePaceLbsPerWeekAbs: effPace };
}
